import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle } from "lucide-react";
import * as React from "react";

import { ApiError, inviteUser, listRegisteredOrganizations } from "@/api/client";
import type { Organization, Role } from "@/api/client";
import { LoadingOverlay } from "@/components/BrandedSpinner";
import { PermissionToggles } from "@/components/PermissionToggles";
import { Button } from "@/components/ui/button";
import {
  CHANNEL_MANAGER,
  CHANNEL_PARTNER,
  INVITE_CTA_LABEL,
  RESETDATA_ADMIN,
  roleFullLabel,
} from "@/lib/role-labels";
import { DEFAULTS_BY_ROLE, type PermKey } from "@/lib/permissions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormError } from "@/components/ui/form-error";
import { usePopperAwareInteractOutside } from "@/lib/radix";

/**
 * The role picker's selectable options. Two roles fan out into two synthetic
 * options each so every conceptual role is a first-class dropdown entry — no
 * hidden checkbox toggles:
 *   - channel_partner → Channel Partner Program Owner (is_org_admin true, the
 *     org's admin; the default) + Channel Partner BDM (false, an individual
 *     rep). CHP-312.
 *   - admin → ResetData Master (is_resetdata_master true; full access, the
 *     default) + ResetData Admin (false; constrained permissions). CHP-427 —
 *     previously admin mapped to a single option plus a "Make ResetData
 *     Master" checkbox, so RA was only reachable by unchecking a box. The RA
 *     option now sits in the dropdown alongside RM, mirroring the PO/BDM split.
 * Every other role maps 1:1. The synthetic option values ("po"/"bdm",
 * "rm"/"ra") are decoded back to (role, is_org_admin, is_resetdata_master) in
 * the dropdown's onValueChange. "Role" is a defensive fallback for an
 * unlabellable role (e.g. "pending", which shouldn't appear in `allowedRoles`).
 */
interface InviteOption {
  value: string;
  label: string;
  role: Role;
  isOrgAdmin: boolean;
  // Only meaningful for role==="admin": true=RM, false=RA. null for every
  // other role (mirrors the is_resetdata_master payload field, which the
  // backend 422s if set on a non-admin invite).
  isResetdataMaster: boolean | null;
}

function inviteOptionsFor(allowedRoles: Role[]): InviteOption[] {
  const options: InviteOption[] = [];
  for (const role of allowedRoles) {
    if (role === "channel_partner") {
      options.push({
        value: "po",
        label: roleFullLabel({ role, is_org_admin: true }) ?? "Role",
        role,
        isOrgAdmin: true,
        isResetdataMaster: null,
      });
      options.push({
        value: "bdm",
        label: roleFullLabel({ role, is_org_admin: false }) ?? "Role",
        role,
        isOrgAdmin: false,
        isResetdataMaster: null,
      });
    } else if (role === "admin") {
      // CHP-427: explicit RM vs RA options (was: one option + a checkbox).
      options.push({
        value: "rm",
        label: roleFullLabel({ role, is_resetdata_master: true }) ?? "Role",
        role,
        isOrgAdmin: true,
        isResetdataMaster: true,
      });
      options.push({
        value: "ra",
        label: roleFullLabel({ role, is_resetdata_master: false }) ?? "Role",
        role,
        isOrgAdmin: true,
        isResetdataMaster: false,
      });
    } else {
      options.push({
        value: role,
        label: roleFullLabel({ role, is_org_admin: true }) ?? "Role",
        role,
        isOrgAdmin: true,
        isResetdataMaster: null,
      });
    }
  }
  return options;
}

export interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string;
  /** Roles the *invoking* user is allowed to invite. CM gets ["channel_partner"]; admin gets all four (minus pending). */
  allowedRoles: Role[];
  /** Override copy for CM "Add Channel Partner" vs admin "Add User". */
  title?: string;
  description?: string;
  /**
   * Optional cache keys to invalidate on success — e.g. the admin
   * dashboard wants ["admin", "users"] to refresh, the manager dashboard
   * wants ["profiles", "manager-team"].
   */
  invalidateOnSuccess?: readonly string[][];
}

export function InviteUserDialog({
  open,
  onOpenChange,
  token,
  allowedRoles,
  title,
  description,
  invalidateOnSuccess,
}: InviteUserDialogProps) {
  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  // CHP-768: when adding a BDM, the inviter picks the BDM's registered Channel
  // Partner org from this dropdown instead of typing a company. "" = nothing
  // selected. POs keep the free-text companyName field; this stays "" for them.
  const [organizationId, setOrganizationId] = React.useState("");
  const [role, setRole] = React.useState<Role>(allowedRoles[0] ?? "channel_partner");
  // When role==='admin', the inviter chooses RM (true) vs RA (false). CHP-427
  // moved this choice out of a "Make ResetData Master" checkbox and into the
  // role dropdown's RM/RA options — the dropdown's onValueChange now sets this
  // directly. Defaults to true so the "all new admins are RM by default"
  // behaviour is preserved (RM is the first admin option). The backend schema
  // 422s if this is set on a non-admin invite, so we pass null when
  // role!=='admin'.
  const [isResetdataMaster, setIsResetdataMaster] = React.useState(true);
  // CHP-312: PO vs BDM for channel_partner invites. true = Program Owner
  // (the org admin; default), false = BDM. Decoded from the role dropdown's
  // synthetic "po"/"bdm" values; only sent on channel_partner invites.
  const [isOrgAdmin, setIsOrgAdmin] = React.useState(true);
  // Slices 5 + 6: per-user permission toggles for CM (Flow C) and RA
  // (Flow D) invites. Initialised to the CM defaults; the `permsRole`
  // effect below resets to the right per-role defaults whenever the
  // active perms role changes. Only sent on CM/RA invites; other
  // shapes pass undefined so the backend schema validator is satisfied.
  const [permissions, setPermissions] = React.useState<Record<PermKey, boolean>>({
    ...DEFAULTS_BY_ROLE.cm,
  });
  // Slices 5 + 6: 2-step flow for CM and RA invites. step 1 = identity
  // form (today's behaviour); step 2 = the "Configure Permissions"
  // screen from Flow C/D. Partner + RM invites stay single-step.
  const [step, setStep] = React.useState<1 | 2>(1);
  const [error, setError] = React.useState<string | null>(null);
  // CHP-781: which step-1 identity fields are missing, so a submit flags ALL of
  // them at once (email + first name + the role-conditional org/company field)
  // rather than surfacing only the first. Keyed by field name -> inline message.
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  // RACCPE-4: holds the invited email once the invite succeeds. Drives the
  // inline success view that replaces the form — the dialog no longer
  // auto-closes on success, so the operator gets explicit confirmation.
  const [success, setSuccess] = React.useState<string | null>(null);
  // CHP-276: also keep the one-time invite link from the API response so the
  // operator can copy it directly — the only recourse if the invitation email
  // is slow or undelivered (the link was otherwise discarded). `copied` drives
  // transient button feedback.
  const [successLink, setSuccessLink] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const queryClient = useQueryClient();

  // Slice 6: derive the role that drives the permissions step:
  //   - channel_manager → 'cm' (Flow C)
  //   - admin + !isResetdataMaster → 'ra' (Flow D)
  //   - otherwise → null (single-step invite, no perms)
  // Used by the step-2 visibility, the defaults-reset effect, the
  // submit-label fork, and the mutation-payload predicate.
  const permsRole: "cm" | "ra" | null =
    role === "channel_manager" ? "cm" : role === "admin" && !isResetdataMaster ? "ra" : null;

  // CHP-768: a BDM invite is a channel_partner where the inviter chose BDM (not
  // PO). The BDM joins an EXISTING registered org by id (dropdown), whereas a
  // PO registers/owns the org via the free-text company_name field.
  const isBdmInvite = role === "channel_partner" && !isOrgAdmin;

  // Fetch the registered-CP org options only when the BDM dropdown is shown.
  // A BDM must join an org that already has an onboarded PO; the list excludes
  // invited-only / shell orgs server-side (has_onboarded_org_admin).
  const registeredOrgsQuery = useQuery<Organization[]>({
    queryKey: ["admin", "organizations", "registered"],
    queryFn: () => listRegisteredOrganizations(token),
    enabled: open && isBdmInvite,
  });
  const registeredOrgs = registeredOrgsQuery.data ?? [];
  // Empty-dropdown: there are no registered CPs yet, so a BDM can't be added —
  // disable the control with the "invite a PO first" hint (CHP-768 default).
  const noRegisteredOrgs = registeredOrgsQuery.isSuccess && registeredOrgs.length === 0;

  React.useEffect(() => {
    if (!open) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setCompanyName("");
      setOrganizationId("");
      setRole(allowedRoles[0] ?? "channel_partner");
      setIsResetdataMaster(true);
      setIsOrgAdmin(true);
      setPermissions({ ...DEFAULTS_BY_ROLE.cm });
      setStep(1);
      setError(null);
      setFieldErrors({});
      setSuccess(null);
      setSuccessLink(null);
      setCopied(false);
    }
  }, [open, allowedRoles]);

  // Slice 6: reset permission toggles to the matching role's defaults
  // whenever the active perms role transitions value (cm ↔ ra ↔ null).
  // Going `ra → null → ra` re-resets defaults; navigating step 1 ↔ step
  // 2 without changing `permsRole` does NOT trigger (the dependency
  // doesn't change), so the existing "Back preserves form state" UX
  // still holds.
  React.useEffect(() => {
    if (permsRole === "cm") {
      setPermissions({ ...DEFAULTS_BY_ROLE.cm });
    } else if (permsRole === "ra") {
      setPermissions({ ...DEFAULTS_BY_ROLE.ra });
    }
    // permsRole === null intentionally leaves `permissions` as-is —
    // the payload predicate omits it, so the lingering state is unused.
  }, [permsRole]);

  // If the inviter switches role/RM-flag such that the perms step
  // no longer applies (e.g. role flips to partner, or RM is re-
  // checked mid-flow on an admin invite), snap back to step 1 so we
  // don't render permission toggles for a single-step invite.
  React.useEffect(() => {
    if (permsRole === null && step !== 1) {
      setStep(1);
    }
  }, [permsRole, step]);

  const mutation = useMutation({
    mutationFn: () =>
      inviteUser(token, {
        email,
        first_name: firstName,
        last_name: lastName,
        // Slice 2 / CHP-768: company_name anchors a *PO's* Organization (the PO
        // registers the org). A BDM joins an existing org by id instead, so its
        // company_name is null (the backend mirrors the canonical Org name).
        // Internal roles (admin/manager) don't belong to an MSP → null.
        company_name: role === "channel_partner" && isOrgAdmin ? companyName.trim() || null : null,
        // CHP-768: the registered-org dropdown choice. Only sent for a BDM
        // invite; the backend 422s organization_id on any non-partner role and
        // rejects it alongside company_name (mutually exclusive).
        organization_id: isBdmInvite ? organizationId || null : null,
        role,
        // CHP-312: PO (is_org_admin true) vs BDM (false). Only meaningful for
        // channel_partner invites; the backend 422s if it's set on any other
        // role, so send null otherwise (mirrors is_resetdata_master).
        is_org_admin: role === "channel_partner" ? isOrgAdmin : null,
        // Slice 4c: only send is_resetdata_master when inviting an admin.
        // Backend schema validator rejects this field on non-admin invites
        // — explicit null keeps non-admin payloads schema-clean.
        is_resetdata_master: role === "admin" ? isResetdataMaster : null,
        // Slices 5 + 6: only send permissions for CM (Flow C) or RA
        // (Flow D — admin + !isResetdataMaster) invites. Backend
        // schema validator 422s if set on any other shape, including
        // RM (admin + isResetdataMaster).
        permissions: permsRole !== null ? permissions : undefined,
      }),
    onSuccess: (data) => {
      // RACCPE-4: surface explicit confirmation rather than auto-closing.
      // The user list/count still refetch (durable proof); the operator
      // closes the dialog themselves via the Done button.
      setError(null);
      setFieldErrors({});
      setSuccess(data.email);
      setSuccessLink(data.invitation_link);
      for (const k of invalidateOnSuccess ?? []) {
        void queryClient.invalidateQueries({ queryKey: k });
      }
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          setError("A user with that email already exists.");
          return;
        }
        if (e.status === 422) {
          setError("Check the form — one or more fields look invalid.");
          return;
        }
        if (e.status === 403) {
          setError("You're not authorised to invite this role.");
          return;
        }
      }
      setError("Couldn't send the invite — please try again.");
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      // CHP-781: validate EVERY required identity field and flag all the missing
      // ones at once, rather than surfacing only the first. The summary `error`
      // names the first failure so the alert still reads sensibly.
      const errors: Record<string, string> = {};
      if (!email) errors.email = "Email is required.";
      if (!firstName) errors.first_name = "First name is required.";
      // CHP-768: a BDM must be linked to an existing registered Channel Partner
      // org (dropdown); a PO registers a new org via the free-text company name.
      if (isBdmInvite) {
        if (!organizationId) {
          errors.organization_id = `Select the ${CHANNEL_PARTNER} this BDM belongs to.`;
        }
      } else if (role === "channel_partner" && !companyName.trim()) {
        // Spec: every PO is invited under a Channel Partner company. The
        // wizard treats this value as locked, so missing it here means the
        // invited partner has no path to set it later.
        errors.company_name = `Company name is required when inviting a ${CHANNEL_PARTNER}.`;
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        // Summary names the first failure in form (DOM) order: first name, then
        // email, then the org/company field.
        const order = ["first_name", "email", "organization_id", "company_name"];
        const firstKey = order.find((k) => k in errors);
        setError(firstKey ? (errors[firstKey] ?? null) : null);
        return;
      }
      setFieldErrors({});
      // Slices 5 + 6: CM and RA invites advance to step 2 (Configure
      // Permissions) instead of dispatching. Partner and RM invites
      // dispatch on step 1 — single-step UX preserved.
      if (permsRole !== null) {
        setStep(2);
        setError(null);
        return;
      }
    }
    mutation.mutate();
  };

  const inviteOptions = inviteOptionsFor(allowedRoles);
  // Keyed on synthetic option values so the fanned-out roles are distinct
  // entries even though they share one backend Role: PO/BDM both resolve to
  // channel_partner (is_org_admin), and RM/RA both resolve to admin
  // (is_resetdata_master — CHP-427).
  const roleSelectValue =
    role === "channel_partner"
      ? isOrgAdmin
        ? "po"
        : "bdm"
      : role === "admin"
        ? isResetdataMaster
          ? "rm"
          : "ra"
        : role;
  // CHP-447: drive the picker off the RENDERED option count, not the raw
  // allowedRoles length. "Add Channel Partner" passes one allowedRole
  // (channel_partner) that expands to TWO options (PO + BDM) — keying on
  // allowedRoles.length hid the picker and locked the invite to PO, so a BDM
  // could no longer be invited directly. inviteOptions.length > 1 keeps the
  // picker visible whenever there's a real choice.
  const showRolePicker = inviteOptions.length > 1;
  const headingTitle =
    title ??
    (allowedRoles.length === 1 && allowedRoles[0] === "channel_partner"
      ? INVITE_CTA_LABEL.partner_invite
      : INVITE_CTA_LABEL.user_invite);

  // Submit-button label varies by step + perms-role to surface what
  // happens next: "Next" advances within the wizard (CM or RA invite),
  // "Send Invite" / "Confirm permissions & dispatch invite" fire
  // the API call.
  const submitLabel = mutation.isPending
    ? "Sending…"
    : step === 2
      ? "Confirm permissions & dispatch invite"
      : permsRole !== null
        ? "Next"
        : "Send invite";

  // RACCPE-4: while the invite is in flight, block dismissal (Esc, overlay
  // click, the X button, Cancel) so a mistimed close can't hide the
  // outcome. React-level only — the request isn't cancelled, so this is
  // about reliable feedback, not data safety. On error `isPending` drops
  // back to false, so Cancel/Esc work again and the dialog can't deadlock.
  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };
  const blockWhilePending = (e: Event) => {
    if (mutation.isPending) e.preventDefault();
  };
  // review-r2 (f8d874a9): the "Role" Select popper portals outside the dialog,
  // so dismissing it was read as an outside-click and closed the whole invite
  // dialog. Swallow that first outside-interaction while a popper is open
  // (CHP-422 helper), in addition to the existing block-while-pending guard.
  const onPopperAwareInteractOutside = usePopperAwareInteractOutside();
  const blockOutside = (e: Event) => {
    blockWhilePending(e);
    onPopperAwareInteractOutside(e);
  };

  // CHP-276: copy the one-time invite link to the clipboard. Best-effort —
  // navigator.clipboard can reject (insecure context / permissions); on
  // failure we leave the label unchanged and the operator can still select
  // the text in the read-only field manually.
  const copyInviteLink = async () => {
    if (!successLink) return;
    try {
      await navigator.clipboard.writeText(successLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent onEscapeKeyDown={blockWhilePending} onInteractOutside={blockOutside}>
        {/* CHP-314: invite dispatch is slow (Authentik user + link + email).
            Wash the dialog with the branded spinner so the wait reads as
            "working", not frozen. */}
        {mutation.isPending ? <LoadingOverlay label="Sending invitation…" /> : null}
        <DialogHeader>
          {/* CHP-291: the success state reads "Invitation Sent", not the
              entry CTA the dialog opened with. */}
          <DialogTitle>{success ? "Invitation Sent" : headingTitle}</DialogTitle>
          {success ? (
            <DialogDescription>The invite is on its way.</DialogDescription>
          ) : step === 2 ? (
            <DialogDescription>
              Permissions are defaulted to the ResetData preferred criteria.
            </DialogDescription>
          ) : description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription>
              We'll email an invitation link with a one-time password reset.
            </DialogDescription>
          )}
        </DialogHeader>
        {success ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-success-border bg-success-muted p-3">
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
              <p className="text-sm text-success-muted-foreground" role="status">
                Invite sent to {success}.
              </p>
            </div>
            {/* CHP-767: canonical invite-sent copy. The link is a 3-day
                Authentik recovery token (CHP-362, extended CHP-1385), so an
                unused link must be resent; one consolidated junk-folder nudge
                (CHP-376) replaces the former separate paragraph. */}
            <p className="text-sm text-muted-foreground">
              This link expires in 3 days. If it's not used in time, you'll need to resend the
              invite.
            </p>
            <p className="text-sm text-muted-foreground">
              Tell the invitee to check their junk folder if it doesn't arrive shortly.
            </p>
            {/* CHP-276: surface the one-time link so onboarding still works if
                the email is slow or undelivered. Previously the API returned
                this link and the dialog threw it away, leaving the operator no
                recourse. Read-only + select-on-focus so it's easy to copy by
                hand even if the clipboard API is unavailable. */}
            {successLink ? (
              <div className="space-y-2">
                <Label htmlFor="invite-link" className="text-sm font-medium">
                  One-time invite link
                </Label>
                <p className="text-xs text-muted-foreground">
                  If the email doesn't arrive, share this link manually:
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="invite-link"
                    readOnly
                    value={successLink}
                    className="font-mono text-xs"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => void copyInviteLink()}
                  >
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            ) : null}
            <DialogFooter className="pt-2">
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // CHP-781: noValidate so OUR JS validation flags every missing required
          // field at once instead of native HTML blocking on the first empty
          // required input.
          <form noValidate onSubmit={submit} className="space-y-3">
            {step === 1 ? (
              <>
                {/* Role first (RACCPE-17): the role choice reveals the
                    role-dependent Company field below (and, for CM/RA, the
                    step-2 permissions screen), so it leads the form. DOM order
                    = tab order (WCAG 2.4.3). */}
                {showRolePicker ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-role">Role</Label>
                    <Select
                      value={roleSelectValue}
                      onValueChange={(v) => {
                        const opt = inviteOptions.find((o) => o.value === v);
                        if (!opt) return;
                        setRole(opt.role);
                        setIsOrgAdmin(opt.isOrgAdmin);
                        // CHP-427: admin options carry RM vs RA; restore the
                        // RM default for any non-admin role so a later switch
                        // back to admin starts from RM.
                        setIsResetdataMaster(opt.isResetdataMaster ?? true);
                      }}
                    >
                      <SelectTrigger id="invite-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {inviteOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-first">First name</Label>
                    <Input
                      id="invite-first"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      aria-invalid={"first_name" in fieldErrors}
                      aria-describedby={
                        "first_name" in fieldErrors ? "invite-first-error" : undefined
                      }
                    />
                    <FormError id="invite-first-error" inline>
                      {fieldErrors.first_name}
                    </FormError>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-last">Last name</Label>
                    <Input
                      id="invite-last"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="invite-email">Work email</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    aria-invalid={"email" in fieldErrors}
                    aria-describedby={"email" in fieldErrors ? "invite-email-error" : undefined}
                  />
                  <FormError id="invite-email-error" inline>
                    {fieldErrors.email}
                  </FormError>
                </div>
                {/* CHP-768: a BDM joins an EXISTING registered Channel Partner
                    org (dropdown of registered CPs); a PO registers a NEW org
                    via the free-text company field. */}
                {isBdmInvite ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-org">{CHANNEL_PARTNER}</Label>
                    <Select
                      value={organizationId}
                      onValueChange={(v) => setOrganizationId(v)}
                      disabled={noRegisteredOrgs || registeredOrgsQuery.isLoading}
                    >
                      <SelectTrigger
                        id="invite-org"
                        aria-label={CHANNEL_PARTNER}
                        aria-invalid={"organization_id" in fieldErrors}
                        aria-describedby={
                          "organization_id" in fieldErrors ? "invite-org-error" : undefined
                        }
                      >
                        <SelectValue
                          placeholder={
                            registeredOrgsQuery.isLoading
                              ? `Loading ${CHANNEL_PARTNER}s…`
                              : `Select the ${CHANNEL_PARTNER}…`
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {registeredOrgs.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormError id="invite-org-error" inline>
                      {fieldErrors.organization_id}
                    </FormError>
                    {noRegisteredOrgs ? (
                      <p className="text-xs text-muted-foreground" data-testid="no-registered-orgs">
                        {`No registered ${CHANNEL_PARTNER}s yet — invite a Program Owner first.`}
                      </p>
                    ) : null}
                  </div>
                ) : role === "channel_partner" ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-company">Company</Label>
                    <Input
                      id="invite-company"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="MSP / reseller name"
                      required
                      aria-invalid={"company_name" in fieldErrors}
                      aria-describedby={
                        "company_name" in fieldErrors ? "invite-company-error" : undefined
                      }
                    />
                    <FormError id="invite-company-error" inline>
                      {fieldErrors.company_name}
                    </FormError>
                  </div>
                ) : null}
              </>
            ) : (
              <fieldset className="space-y-4">
                <legend className="sr-only">{`${
                  permsRole === "ra" ? RESETDATA_ADMIN : CHANNEL_MANAGER
                } permissions`}</legend>
                <p className="text-sm text-muted-foreground">
                  {`These will be the permissions settings for ${firstName || email} (${
                    permsRole === "ra" ? RESETDATA_ADMIN : CHANNEL_MANAGER
                  })`}
                </p>
                {/* CHP-296 toggles, extracted to <PermissionToggles> (CHP-322)
                    so the invite step and the edit-permissions dialog share one
                    render. idPrefix keeps the existing test ids stable. */}
                <PermissionToggles
                  permissions={permissions}
                  onChange={(key, checked) =>
                    setPermissions((prev) => ({ ...prev, [key]: checked }))
                  }
                  idPrefix="invite-perm"
                />
              </fieldset>
            )}
            {error ? <FormError>{error}</FormError> : null}
            <DialogFooter className="pt-2">
              {step === 2 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStep(1);
                    setError(null);
                  }}
                >
                  Back
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={mutation.isPending}>
                {submitLabel}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
